import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Search, BookOpen, Download, Clock, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const FOCUS_OPTIONS = ["Tecnica", "Tattica", "Possesso", "Atletica", "Conclusione", "Portieri"];

const LEVEL_OPTIONS = [
  "Piccoli Amici", "Primi Calci", "Pulcini", "Esordienti",
  "Giovanissimi", "Allievi", "Juniores", "Prima Squadra",
];

/**
 * Public library browser. Lists ~30 FIGC SGS drills bundled with the app.
 * Lets the coach filter and import any drill into their team library with
 * one click.
 */
export default function PublicLibraryDialog({ open, onOpenChange, teamId, onImported }) {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [query, setQuery] = useState("");
  const [focus, setFocus] = useState("");
  const [level, setLevel] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/exercises/public")
      .then(({ data }) => setAll(data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Errore caricamento libreria"))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    let xs = all;
    if (focus) xs = xs.filter((e) => e.focus === focus);
    if (level) xs = xs.filter((e) => Array.isArray(e.levels) && e.levels.includes(level));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      xs = xs.filter(
        (e) => e.name.toLowerCase().includes(q) || (e.description || "").toLowerCase().includes(q)
      );
    }
    return xs;
  }, [all, focus, level, query]);

  const importDrill = async (ex) => {
    if (!teamId) return toast.error("Seleziona prima una squadra");
    setImportingId(ex.id);
    try {
      await api.post(`/exercises/import-public/${ex.id}?team_id=${teamId}`);
      toast.success(`«${ex.name}» importato nella tua libreria`);
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore importazione");
    } finally {
      setImportingId(null);
    }
  };

  const focusColor = (f) => ({
    Tecnica: "bg-sky-100 text-sky-700",
    Tattica: "bg-emerald-100 text-emerald-700",
    Possesso: "bg-emerald-100 text-emerald-700",
    Atletica: "bg-orange-100 text-orange-700",
    Conclusione: "bg-rose-100 text-rose-700",
    Portieri: "bg-purple-100 text-purple-700",
  }[f] || "bg-slate-100 text-slate-700");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] flex flex-col" data-testid="public-library-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen size={18} className="text-emerald-600" /> Libreria pubblica FIGC SGS
            <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700 border-amber-200">
              <Sparkles size={11} className="mr-1" /> Curata
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Drill standard del Settore Giovanile e Scolastico FIGC. Click su &quot;Importa&quot; per
            duplicare l&apos;esercizio nella tua libreria personale (poi potrai modificarlo).
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca per nome o descrizione…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 h-9"
              data-testid="public-library-search"
            />
          </div>
          <select
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            className="h-9 px-2 rounded-md border border-slate-200 text-sm bg-white"
            data-testid="public-library-focus"
          >
            <option value="">Tutti i focus</option>
            {FOCUS_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="h-9 px-2 rounded-md border border-slate-200 text-sm bg-white"
            data-testid="public-library-level"
          >
            <option value="">Tutte le categorie</option>
            {LEVEL_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <Badge variant="outline" className="text-xs">
            {filtered.length} / {all.length}
          </Badge>
        </div>

        {/* Results */}
        <div className="overflow-y-auto flex-1 -mx-6 px-6 py-2 mt-2">
          {loading ? (
            <div className="text-sm text-slate-500 py-12 text-center">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-slate-500 py-12 text-center">
              Nessun esercizio trovato con questi filtri.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filtered.map((ex) => (
                <div
                  key={ex.id}
                  className="p-4 rounded-xl border border-slate-200 bg-white hover:border-emerald-300 hover:shadow-sm transition"
                  data-testid={`public-drill-${ex.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-heading font-bold text-sm leading-tight">{ex.name}</h3>
                    <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 shrink-0">
                      <Clock size={10} /> {ex.duration}&apos;
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${focusColor(ex.focus)}`}>
                      {ex.focus}
                    </span>
                    {(ex.levels || []).slice(0, 3).map((l) => (
                      <span key={l} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {l}
                      </span>
                    ))}
                    {(ex.levels || []).length > 3 && (
                      <span className="text-[10px] text-slate-400">+{ex.levels.length - 3}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 line-clamp-3 mb-3">
                    {ex.description}
                  </p>
                  <Button
                    size="sm"
                    onClick={() => importDrill(ex)}
                    disabled={importingId === ex.id}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    data-testid={`import-${ex.id}`}
                  >
                    <Download size={12} className="mr-1.5" />
                    {importingId === ex.id ? "Importazione…" : "Importa nella mia libreria"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
